import api from './api';

export interface OperationsHealth {
  whatsapp: {
    totalAccounts: number;
    activeAccounts: number;
    connectedAccounts: number;
    readyAccounts: number;
    monitoredGroupCount: number;
    knownGroupCount: number;
    lastActivity?: string | null;
    recentErrors: string[];
  };
  aiQueue: {
    queueSize: number;
    processing: boolean;
    aiEnabled: boolean;
    filterStats?: {
      totalMessages?: number;
      filteredOut?: number;
      sentToAI?: number;
      filterEfficiency?: number;
      estimatedCostSaved?: string;
    };
  };
  rides: {
    activeRides: number;
    ridesToday: number;
    lastRideProcessed?: any;
  };
  notifications: {
    firebaseConfigured: boolean;
    activeDrivers: number;
    driversWithFcm: number;
  };
  payments: {
    currency: string;
    gateways: Record<string, { enabled: boolean; mode: string }>;
  };
}

export interface AdminPlan {
  _id: string;
  name: string;
  slug: string;
  description: string;
  price: number;
  monthlyLeadLimit: number | null;
  currency: string;
  billingInterval: 'monthly' | 'yearly';
  topUpAllowed: boolean;
  earlyAccessMinutes: number;
  teamSeats: number;
  canExport: boolean;
  prioritySupport: boolean;
  features: string[];
  isActive: boolean;
  sortOrder: number;
}

export interface RevenueSummary {
  totals: {
    totalRevenue: number;
    paidOrders: number;
    monthRevenue: number;
    monthPaidOrders: number;
    activeSubscriptions: number;
    unlocksThisMonth: number;
  };
  byGateway: { gateway: string; revenue: number; paidOrders: number }[];
  ordersByStatus: Record<string, number>;
}

export interface LeadReport {
  _id: string;
  action: 'reported_not_relevant' | 'reported_invalid';
  reason: string;
  metadata?: Record<string, any>;
  createdAt: string;
  userId?: {
    _id: string;
    name: string;
    email: string;
    bonusUnlocks: number;
  };
  rideId?: {
    _id: string;
    aiAnalysis?: {
      from?: string;
      to?: string;
      time?: string;
      price?: string;
    };
    status: string;
    priority: string;
  };
}

export interface ComparisonFeature {
  _id: string;
  name: string;
  planIds: string[];
  sortOrder: number;
}

export interface Coupon {
  _id: string;
  code: string;
  discountType: 'percent' | 'fixed';
  discountValue: number;
  validUntil?: string;
  maxRedemptions?: number | null;
  redeemedCount: number;
  isActive: boolean;
}

export interface DriverAnalytics {
  overview: {
    totalDrivers: number;
    activeDrivers: number;
    bannedDrivers: number;
    onlineDrivers: number;
    verifiedDrivers: number;
    twoFactorDrivers: number;
    withFcmDrivers: number;
    newThisMonth: number;
    newPrevMonth: number;
    newLast7Days: number;
    growthRate: number;
    monthUnlocks: number;
    avgUnlocksPerActiveDriver: number;
  };
  signupsByDay: { date: string; count: number }[];
  planDistribution: {
    planId: string;
    planName: string;
    planSlug: string;
    price: number;
    count: number;
  }[];
  topUnlockers: {
    userId: string;
    name: string;
    email: string;
    unlocks: number;
    isOnline: boolean;
    isActive: boolean;
  }[];
  leadActionBreakdown: { _id: string; count: number }[];
  topRevenue: {
    userId: string;
    name: string;
    email: string;
    totalSpent: number;
    orders: number;
  }[];
}

export interface DriverDetail {
  driver: {
    _id: string;
    name: string;
    email: string;
    phone?: string;
    role: string;
    isActive: boolean;
    isOnline: boolean;
    lastOnlineAt?: string;
    lastLogin?: string;
    emailVerified: boolean;
    twoFactorEnabled: boolean;
    googleId?: string;
    referralCode?: string;
    bonusUnlocks: number;
    createdAt: string;
    fcmToken?: string;
  };
  subscription: {
    id: string;
    plan: any;
    status: string;
    startsAt: string;
    endsAt?: string;
    canceledAt?: string;
  } | null;
  subscriptionHistory: any[];
  credits: {
    planName: string;
    planSlug: string;
    used: number;
    baseMonthlyLeadLimit: number | null;
    planRemaining: number | null;
    walletCredits: number;
    totalAvailable: number | null;
    bonusUnlocks: number;
    monthlyLeadLimit: number | null;
    remaining: number | null;
    lifetimeUnlocks: number;
  };
  unlocksByMonth: { month: string; count: number }[];
  activity: {
    last90Days: Record<string, number>;
    recentUnlocks: {
      unlockId: string;
      unlockedAt: string;
      ride: any;
    }[];
  };
  creditHistory: {
    _id: string;
    type: string;
    amount: number;
    reason: string;
    createdAt: string;
    adminId?: { _id: string; name: string; email: string };
  }[];
  payments: {
    totalSpent: number;
    orders: number;
    lastPayment: string | null;
  };
  referralCount: number;
}

export const adminService = {
  async getOperationsHealth(): Promise<OperationsHealth> {
    const response = await api.get('/admin/operations/health');
    return response.data;
  },

  async getRevenue(): Promise<RevenueSummary> {
    const response = await api.get('/admin/revenue');
    return response.data;
  },

  async getPlans(): Promise<AdminPlan[]> {
    const response = await api.get('/admin/plans');
    return response.data.plans;
  },

  async updatePlan(id: string, plan: Partial<AdminPlan>): Promise<AdminPlan> {
    const response = await api.patch(`/admin/plans/${id}`, plan);
    return response.data.plan;
  },

  async updateFreePlanSettings(plan: Pick<AdminPlan, 'name' | 'description' | 'features'>): Promise<AdminPlan> {
    const response = await api.patch('/admin/plans/free-settings', plan);
    return response.data.plan;
  },

  async createPlan(plan: Partial<AdminPlan>): Promise<AdminPlan> {
    const response = await api.post('/admin/plans', plan);
    return response.data.plan;
  },

  async deletePlan(id: string): Promise<void> {
    await api.delete(`/admin/plans/${id}`);
  },

  async getPlanSubscriberCount(id: string): Promise<number> {
    const response = await api.get(`/admin/plans/${id}/subscriber-count`);
    return response.data.count ?? 0;
  },

  async getLeadReports(refunded = false): Promise<LeadReport[]> {
    const response = await api.get('/admin/lead-reports', {
      params: { refunded: refunded ? 'true' : 'false', limit: 10 },
    });
    return response.data.reports;
  },

  async refundLeadReport(actionId: string, amount = 1): Promise<void> {
    await api.post(`/admin/lead-reports/${actionId}/refund`, { amount });
  },

  async getCoupons(): Promise<Coupon[]> {
    const response = await api.get('/admin/coupons');
    return response.data.coupons;
  },

  async createCoupon(coupon: Partial<Coupon>): Promise<Coupon> {
    const response = await api.post('/admin/coupons', coupon);
    return response.data.coupon;
  },

  async getDriverAnalytics(): Promise<DriverAnalytics> {
    const response = await api.get('/admin/drivers/analytics');
    return response.data;
  },

  async getDriverDetail(id: string): Promise<DriverDetail> {
    const response = await api.get(`/admin/drivers/${id}`);
    return response.data;
  },

  async getComparisonFeatures(): Promise<ComparisonFeature[]> {
    const response = await api.get('/admin/comparison-features');
    return response.data.features;
  },

  async createComparisonFeature(payload: { name: string; planIds: string[]; sortOrder: number }): Promise<ComparisonFeature> {
    const response = await api.post('/admin/comparison-features', payload);
    return response.data.feature;
  },

  async updateComparisonFeature(id: string, payload: Partial<Pick<ComparisonFeature, 'name' | 'planIds' | 'sortOrder'>>): Promise<ComparisonFeature> {
    const response = await api.patch(`/admin/comparison-features/${id}`, payload);
    return response.data.feature;
  },

  async deleteComparisonFeature(id: string): Promise<void> {
    await api.delete(`/admin/comparison-features/${id}`);
  },

  async updateDriver(
    id: string,
    payload: {
      name?: string;
      email?: string;
      phone?: string;
      role?: string;
      isActive?: boolean;
      isOnline?: boolean;
      emailVerified?: boolean;
      bonusUnlocks?: number;
    }
  ): Promise<{ driver: DriverDetail['driver'] }> {
    const response = await api.patch(`/admin/drivers/${id}`, payload);
    return response.data;
  },

  // ── Driver subscription management ──────────────────────────────────────

  async grantSubscription(driverId: string, payload: { planId: string; durationDays?: number | null }): Promise<{ message: string }> {
    const response = await api.post(`/admin/drivers/${driverId}/subscription/grant`, payload);
    return response.data;
  },

  async extendSubscription(driverId: string, payload: { days: number }): Promise<{ message: string }> {
    const response = await api.post(`/admin/drivers/${driverId}/subscription/extend`, payload);
    return response.data;
  },

  async cancelDriverSubscription(driverId: string, payload: { immediate: boolean }): Promise<{ message: string }> {
    const response = await api.post(`/admin/drivers/${driverId}/subscription/cancel`, payload);
    return response.data;
  },

  // ── Payment orders & refunds ────────────────────────────────────────────

  async getPaymentOrders(params: {
    page?: number;
    limit?: number;
    status?: string;
    orderType?: string;
    gateway?: string;
    search?: string;
  }): Promise<{ orders: AdminPaymentOrder[]; pagination: { page: number; limit: number; total: number; pages: number } }> {
    const response = await api.get('/admin/payment-orders', { params });
    return response.data;
  },

  async refundPaymentOrder(
    orderId: string,
    payload: { mode: 'gateway' | 'wallet'; amount?: number; reason?: string; cancelSubscription?: boolean }
  ): Promise<{ message: string; order: AdminPaymentOrder }> {
    const response = await api.post(`/admin/payment-orders/${orderId}/refund`, payload);
    return response.data;
  },

  async getRefundStatus(orderId: string): Promise<{ order: AdminPaymentOrder; gatewayStatus?: string }> {
    const response = await api.get(`/admin/payment-orders/${orderId}/refund-status`);
    return response.data;
  },

  async testCashfreeWebhook(): Promise<{
    url: string;
    reachable: boolean;
    httpStatus?: number;
    latencyMs?: number;
    signatureAccepted: boolean;
    error?: string;
    hint?: string;
  }> {
    const response = await api.post('/admin/payments/cashfree-webhook-test');
    return response.data;
  },
};

export interface AdminPaymentOrder {
  id: string;
  driver: { id: string; name: string; email: string } | null;
  orderType: 'plan' | 'top_up';
  itemName: string;
  gateway: string;
  amount: number;
  discountAmount: number;
  couponCode: string;
  currency: string;
  status: 'created' | 'paid' | 'failed' | 'refunded';
  subscriptionStatus: 'pending' | 'created' | 'failed';
  refund: {
    mode: 'gateway' | 'wallet';
    amount: number;
    credits?: number;
    status: 'pending' | 'processed' | 'failed';
    refundedAt?: string;
    reason?: string;
  } | null;
  createdAt: string;
  paidAt: string | null;
}
